import { useState } from "react";
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { toolbarTier } from "@/partials/document-renderer/components/document-toolbar/documentToolbarTiers";

export interface DocumentSearchProps {
  query: string;
  setQuery: (query: string) => void;
  currentIndex: number;
  totalMatches: number;
  goToNext: () => void;
  goToPrevious: () => void;
  clearSearch: () => void;
}

interface DocumentSearchControlsProps {
  searchProps: DocumentSearchProps;
  inputId: string;
  triggerId: string;
  previousButtonId: string;
  nextButtonId: string;
}

/**
 * The field fills the popover, and takes a fixed width in the toolbar so the
 * groups beside it keep their place as the document column narrows.
 */
type DocumentSearchFieldsProps = Omit<
  DocumentSearchControlsProps,
  "triggerId"
> & { fieldClassName: string };

const DocumentSearchFields = ({
  searchProps,
  inputId,
  previousButtonId,
  nextButtonId,
  fieldClassName,
}: DocumentSearchFieldsProps) => (
  <>
    <div className={cn("relative min-w-0", fieldClassName)}>
      <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
      <Input
        type="text"
        placeholder="Søg i dokument..."
        value={searchProps.query}
        onChange={(e) => searchProps.setQuery(e.target.value)}
        className="h-7 w-full min-w-[10rem] pl-7 pr-7 text-xs"
        id={inputId}
      />
      {searchProps.query && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-0.5 top-1/2 -translate-y-1/2 size-5"
          onClick={searchProps.clearSearch}
          aria-label="Ryd søgning"
        >
          <X className="size-3" />
        </Button>
      )}
    </div>

    {searchProps.query.length >= 2 && (
      <span className="text-xs text-muted-foreground min-w-12 text-center shrink-0">
        {searchProps.totalMatches > 0
          ? `${searchProps.currentIndex + 1}/${searchProps.totalMatches}`
          : "0"}
      </span>
    )}

    {searchProps.totalMatches > 0 && (
      <div className="flex items-center shrink-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              onClick={searchProps.goToPrevious}
              id={previousButtonId}
              aria-label="Forrige match"
            >
              <ChevronUp className="size-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Forrige</p>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              onClick={searchProps.goToNext}
              id={nextButtonId}
              aria-label="Næste match"
            >
              <ChevronDown className="size-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Næste</p>
          </TooltipContent>
        </Tooltip>
      </div>
    )}
  </>
);

export const DocumentSearchControls = ({
  searchProps,
  inputId,
  triggerId,
  previousButtonId,
  nextButtonId,
}: DocumentSearchControlsProps) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const hasActiveSearch = searchProps.query.length > 0;

  return (
    <>
      <div className={toolbarTier.belowFull}>
        <Popover open={searchOpen} onOpenChange={setSearchOpen}>
          <PopoverTrigger asChild>
            <Button
              variant={hasActiveSearch ? "secondary" : "outline"}
              size="icon"
              className="size-7"
              id={triggerId}
              aria-label="Søg i dokument"
            >
              <Search className="size-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-3">
            <div className="flex items-center gap-2">
              <DocumentSearchFields
                searchProps={searchProps}
                inputId={inputId}
                previousButtonId={previousButtonId}
                nextButtonId={nextButtonId}
                fieldClassName="flex-1"
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className={cn("items-center gap-1", toolbarTier.fromFullFlex)}>
        <DocumentSearchFields
          searchProps={searchProps}
          inputId={inputId}
          previousButtonId={previousButtonId}
          nextButtonId={nextButtonId}
          fieldClassName="w-44"
        />
      </div>
    </>
  );
};
